import { useSettingsStore, useCrmStore, DEFAULT_SETTINGS } from '@/stores';
import { TemplateEditor, type TemplateVariableOption } from './TemplateEditor';
import { Button } from '@/components/common/Button';
import { getCurrencySymbol } from '@/utils/currency';
import { inferTemplateCategoryFromRules, getTemplateCategoryLabel } from '@/utils/segmentRuleInference';
import {
  Sliders, Award, AlertCircle, Calendar, Users, Cake,
  Sparkles, UserPlus, Star, Clock, DollarSign, FolderOpen
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Settings } from '@/types/settings';

function getBaseTemplateForCategory(
  category: ReturnType<typeof inferTemplateCategoryFromRules>,
  s: Settings
): string {
  switch (category) {
    case 'birthday':
      return s.msgTemplateBirthday;
    case 'top_spender':
      return s.msgTemplateTopSpenders;
    case 'vip':
      return s.msgTemplateVip;
    case 'lapsed':
      return s.msgTemplateLapsed;
    case 'new':
    case 'first_visit':
      return s.msgTemplateNewClients;
    case 'default':
    default:
      return s.templateCrmDefault;
  }
}

export function CrmConfig() {
  const settings = useSettingsStore();
  const segments = useCrmStore((s) => s.segments);
  const sym = getCurrencySymbol(settings.currencyCode);

  const crmVars: TemplateVariableOption[] = [
    // Cliente
    { token: 'cliente', label: 'Nombre cliente (inteligente)', category: 'cliente' },
    { token: 'primer_nombre', label: 'Primer nombre (exacto)', category: 'cliente' },
    { token: 'nombre_completo', label: 'Nombre completo', category: 'cliente' },
    { token: 'apellido', label: 'Apellidos', category: 'cliente' },

    // Cita / Especialista
    { token: 'profesional', label: 'Especialista / Profesional', category: 'cita' },
    { token: 'ultimo_servicio', label: 'Último servicio', category: 'cita' },
    { token: 'ultima_sede', label: 'Última sede', category: 'cita' },

    // Precios
    { token: 'deuda', label: `Saldo deudor (${sym})`, category: 'precios' },
    { token: 'deuda_bs', label: 'Saldo deudor en Bs.', category: 'precios' },
    { token: 'consumo', label: `Total consumo (${sym})`, category: 'precios' },
    { token: 'consumo_bs', label: 'Total consumo en Bs.', category: 'precios' },

    // Historial
    { token: 'dias', label: 'Días ausente', category: 'historial' },
    { token: 'total_citas', label: 'Total citas', category: 'historial' },
    { token: 'cumpleanos', label: 'Fecha cumpleaños', category: 'historial' },
    { token: 'dias_cumpleanos', label: 'Días para cumpleaños', category: 'historial' },
    { token: 'antiguedad', label: 'Antigüedad cliente', category: 'historial' },
    { token: 'ultima_cita', label: 'Fecha última cita', category: 'historial' },

    // General
    { token: 'saludo', label: 'Saludo contextual', category: 'general' },
    { token: 'negocio', label: 'Nombre negocio', category: 'general' },
    { token: 'firma', label: 'Texto de cierre', category: 'general' },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Reglas Métricas de Segmentación */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <Sliders size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Reglas & Umbrales de Segmentación</h3>
            <p className="text-[11px] text-text-muted">Métricas operativas aplicadas a clientes para determinar inactividad, VIP y deudores</p>
          </div>
        </div>

        {/* Umbral de Inactividad */}
        <div className="space-y-1.5 pt-2 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary flex items-center justify-between">
            <span>Umbral de Inactividad (Días sin visita)</span>
            <span className="font-bold text-brand-primary">{settings.inactivityDays} días</span>
          </label>
          <input
            type="range"
            min="15"
            max="180"
            step="5"
            value={settings.inactivityDays}
            onChange={(e) => settings.setSetting('inactivityDays', parseInt(e.target.value, 10))}
            className="w-full accent-brand-primary cursor-pointer"
          />
        </div>

        {/* Ventana de Búsqueda */}
        <div className="space-y-1.5 pt-2 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary flex items-center justify-between">
            <span className="flex items-center gap-1">
              <Calendar size={13} className="text-brand-primary" />
              <span>Ventana de Búsqueda de Historial</span>
            </span>
            <span className="font-bold text-brand-primary">{settings.campaignRangeDays} días</span>
          </label>
          <input
            type="range"
            min="60"
            max="730"
            step="30"
            value={settings.campaignRangeDays}
            onChange={(e) => settings.setSetting('campaignRangeDays', parseInt(e.target.value, 10))}
            className="w-full accent-brand-primary cursor-pointer"
          />
          <p className="text-[11px] text-text-muted">Rango de fechas para consultar el historial de citas y retención</p>
        </div>

        {/* Criterio VIP */}
        <div className="space-y-2 pt-2 border-t border-border-light">
          <span className="text-xs font-medium text-text-primary flex items-center gap-1">
            <Award size={13} className="text-brand-primary" />
            <span>Criterio para Clientes Frecuentes / VIP</span>
          </span>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-text-muted block mb-1">Mínimo Citas</label>
              <input
                type="number"
                value={settings.vipMinAppointments}
                onChange={(e) => settings.setSetting('vipMinAppointments', parseInt(e.target.value, 10) || 1)}
                className="w-full px-2.5 py-1.5 bg-surface-secondary border border-border rounded-lg text-xs"
              />
            </div>
            <div>
              <label className="text-[11px] text-text-muted block mb-1">Mínimo Ventas ({sym})</label>
              <input
                type="number"
                value={settings.vipMinSales}
                onChange={(e) => settings.setSetting('vipMinSales', parseFloat(e.target.value) || 100)}
                className="w-full px-2.5 py-1.5 bg-surface-secondary border border-border rounded-lg text-xs"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <span className="text-[11px] text-text-muted">Cumplir:</span>
            <label className="flex items-center gap-1.5 text-xs text-text-primary cursor-pointer">
              <input
                type="radio"
                name="vipRelation"
                value="or"
                checked={settings.vipRelation === 'or'}
                onChange={() => settings.setSetting('vipRelation', 'or')}
                className="accent-brand-primary"
              />
              Cualquiera (O)
            </label>
            <label className="flex items-center gap-1.5 text-xs text-text-primary cursor-pointer">
              <input
                type="radio"
                name="vipRelation"
                value="and"
                checked={settings.vipRelation === 'and'}
                onChange={() => settings.setSetting('vipRelation', 'and')}
                className="accent-brand-primary"
              />
              Ambos (Y)
            </label>
          </div>
        </div>

        {/* Mínimo de Deuda */}
        <div className="space-y-1.5 pt-2 border-t border-border-light">
          <label className="text-xs font-medium text-text-primary flex items-center gap-1">
            <AlertCircle size={13} className="text-brand-danger" />
            <span>Mínimo de Deuda para Saldo Pendiente ({sym})</span>
          </label>
          <input
            type="number"
            step="0.5"
            value={settings.debtorMinAmount}
            onChange={(e) => settings.setSetting('debtorMinAmount', parseFloat(e.target.value) || 0)}
            className="w-full px-2.5 py-1.5 bg-surface-secondary border border-border rounded-lg text-xs"
          />
        </div>
      </div>

      {/* 2. Plantilla CRM General Fallback */}
      <div className="space-y-3">
        <div className="bg-surface border border-border rounded-xl p-4 space-y-1 shadow-xs">
          <h3 className="text-sm font-semibold text-text-primary">Plantilla Predeterminada (Uso general)</h3>
          <p className="text-xs text-text-muted">
            Mensaje predeterminado cuando un cliente no posee una intención o regla de comportamiento específica asignada.
          </p>
        </div>

        <TemplateEditor
          title="Plantilla CRM General (Contacto y Saludo)"
          template={settings.templateCrmDefault}
          defaultTemplate={DEFAULT_SETTINGS.templateCrmDefault}
          onChange={(v) => settings.setSetting('templateCrmDefault', v)}
          variables={crmVars}
          previewType="crm"
          icon={<Users size={15} />}
        />
      </div>

      {/* 3. Plantillas Maestras por Comportamiento */}
      <div className="space-y-3">
        <div className="bg-surface border border-border rounded-xl p-4 space-y-1 shadow-xs">
          <h3 className="text-sm font-semibold text-text-primary">Plantillas Maestras por Comportamiento</h3>
          <p className="text-xs text-text-muted">
            Mensajes automatizados que se sugieren en base a cumpleaños, inactividad, saldo deudor o historial de compras.
          </p>
        </div>

        <TemplateEditor
          title="🎂 Próximos Cumpleaños"
          template={settings.msgTemplateBirthday}
          defaultTemplate={DEFAULT_SETTINGS.msgTemplateBirthday}
          onChange={(v) => settings.setSetting('msgTemplateBirthday', v)}
          variables={crmVars}
          collapsible
          defaultExpanded={true}
          previewType="crm"
          icon={<Cake size={15} className="text-pink-500" />}
        />

        <TemplateEditor
          title="💎 Clientes con Mayor Consumo"
          template={settings.msgTemplateTopSpenders}
          defaultTemplate={DEFAULT_SETTINGS.msgTemplateTopSpenders}
          onChange={(v) => settings.setSetting('msgTemplateTopSpenders', v)}
          variables={crmVars}
          collapsible
          defaultExpanded={false}
          previewType="crm"
          icon={<Sparkles size={15} className="text-amber-500" />}
        />

        <TemplateEditor
          title="👋 Clientes Nuevos & Primera Visita"
          template={settings.msgTemplateNewClients}
          defaultTemplate={DEFAULT_SETTINGS.msgTemplateNewClients}
          onChange={(v) => settings.setSetting('msgTemplateNewClients', v)}
          variables={crmVars}
          collapsible
          defaultExpanded={false}
          previewType="crm"
          icon={<UserPlus size={15} className="text-blue-500" />}
        />

        <TemplateEditor
          title="⭐ Clientes Frecuentes & VIP (Fidelización)"
          template={settings.msgTemplateVip}
          defaultTemplate={DEFAULT_SETTINGS.msgTemplateVip}
          onChange={(v) => settings.setSetting('msgTemplateVip', v)}
          variables={crmVars}
          collapsible
          defaultExpanded={false}
          previewType="crm"
          icon={<Star size={15} className="text-amber-500" />}
        />

        <TemplateEditor
          title="💤 Clientes Inactivos / Ausentes (Reactivación)"
          template={settings.msgTemplateLapsed}
          defaultTemplate={DEFAULT_SETTINGS.msgTemplateLapsed}
          onChange={(v) => settings.setSetting('msgTemplateLapsed', v)}
          variables={crmVars}
          collapsible
          defaultExpanded={false}
          previewType="crm"
          icon={<Clock size={15} className="text-indigo-500" />}
        />

        <TemplateEditor
          title="💰 Saldo Deudor Pendiente"
          template={settings.msgTemplateDebtor}
          defaultTemplate={DEFAULT_SETTINGS.msgTemplateDebtor}
          onChange={(v) => settings.setSetting('msgTemplateDebtor', v)}
          variables={crmVars}
          collapsible
          defaultExpanded={false}
          previewType="crm"
          icon={<DollarSign size={15} className="text-emerald-500" />}
        />
      </div>

      {/* 4. Personalizaciones Específicas por Segmento Fresha */}
      <div className="space-y-4 pt-2">
        <div className="bg-surface border border-border rounded-xl p-4 space-y-1 shadow-xs">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
              <FolderOpen size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Personalizaciones por Segmento de Fresha</h3>
              <p className="text-xs text-text-muted">
                Sobrescribe la plantilla inferida para fijar un mensaje exclusivo en cualquier segmento específico.
              </p>
            </div>
          </div>
        </div>

        {segments.length === 0 ? (
          <div className="p-4 bg-surface border border-border rounded-xl text-center text-xs text-text-muted">
            Cargando segmentos del salón desde Fresha...
          </div>
        ) : (
          <div className="space-y-3">
            {segments.map((seg) => {
              const inferredCat = inferTemplateCategoryFromRules(seg.rules, seg.name, seg.id);
              const inferredLabel = getTemplateCategoryLabel(inferredCat);
              const baseTemplate = getBaseTemplateForCategory(inferredCat, settings);
              const hasOverride = typeof settings.crmSegmentTemplates?.[seg.id] === 'string';
              const currentTemplate = hasOverride ? settings.crmSegmentTemplates[seg.id] : baseTemplate;

              return (
                <div key={seg.id} className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-xs text-text-primary">{seg.name}</span>
                      {typeof seg.count === 'number' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary font-medium">
                          {seg.count} clientes
                        </span>
                      )}
                      <span className={clsx(
                        'text-[10px] px-2 py-0.5 rounded-full border font-medium flex items-center gap-1',
                        hasOverride
                          ? 'bg-brand-primary/10 text-brand-primary border-brand-primary/20'
                          : 'bg-surface-secondary text-text-secondary border-border'
                      )}>
                        <span>{hasOverride ? 'Personalizado' : 'Plantilla sugerida:'}</span>
                        <strong className="text-text-primary">{inferredLabel}</strong>
                      </span>
                    </div>

                    {!hasOverride ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => settings.updateCrmSegmentTemplate(seg.id, baseTemplate)}
                        className="text-xs"
                      >
                        Personalizar mensaje
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => settings.removeCrmSegmentTemplate(seg.id)}
                        className="text-xs"
                      >
                        Restaurar a sugerida
                      </Button>
                    )}
                  </div>

                  {hasOverride && (
                    <div className="pt-2">
                      <TemplateEditor
                        title={`Plantilla Exclusiva: ${seg.name}`}
                        template={currentTemplate}
                        defaultTemplate={baseTemplate}
                        onChange={(val) => settings.updateCrmSegmentTemplate(seg.id, val)}
                        variables={crmVars}
                        previewType="crm"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
